import {
    AfterViewInit,
    Directive,
    ElementRef,
    HostBinding,
    HostListener,
    OnInit,
    inject,
    input,
    model,
    signal,
} from '@angular/core';
import { EMPTY, Observable, forkJoin, map, of } from 'rxjs';

import { NotifyService } from '../lib/services/notify.service';
import { stringEquals } from '@my-aon/utils';

@Directive({
    selector: '[appFileDragAndDrop]',
    exportAs: 'appFileDragAndDrop',
    standalone: true,
})
export class FileDragAndDropDirective implements OnInit, AfterViewInit {
    readonly notify: NotifyService = inject(NotifyService);
    private readonly el = inject(ElementRef);

    readonly $enabled = input<boolean>(true, { alias: 'enabled' });

    readonly $allowedFileExtensions = input<string[]>(['xls', 'xlsx', 'xlsb', 'xlsm'], { alias: 'allowedFileExtensions' });
    readonly $allowedContentTypes = input<string[]>([
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel.sheet.binary.macroEnabled.12',
        'application/vnd.ms-excel.sheet.macroEnabled.12'
    ], { alias: 'allowedContentTypes' });

    readonly $maxFileSize = input<number | undefined>(8, { alias: 'maxFileSize' }); // in MB

    readonly $multiple = input<boolean | undefined>(false, { alias: 'multiple' });

    readonly $maxLimitFiles = input<number>(10, { alias: 'maxLimitFiles' });

    readonly $appRoles = input<string[] | undefined>(undefined, { alias: 'appRoles' });

    get MaxFileSize(): number | null {
        const maxFileSize = this.$maxFileSize();
        return (maxFileSize && maxFileSize * (1024 * 1024)) || null;
    }

    readonly $showBorders = input<boolean | undefined>(true, { alias: 'showBorders' });

    readonly $files = model<File[]>([], { alias: 'files' });

    @HostBinding('style.border-color') private borderColor: string = 'gray';
    @HostBinding('style.border-width') private borderWidth: string = '1px';
    @HostBinding('style.border-style') private borderStyle: string = 'dashed';
    @HostBinding('style.background-color') private backgroundColor: string = '';

    get hasFiles(): boolean {
        const files = this.$files();
        return files && files.length > 0;
    }

    $loadIndicatorVisible = signal<boolean>(false);
    loading(isLoading: boolean) {
        this.$loadIndicatorVisible.set(isLoading);
    }

    private fileInput?: HTMLInputElement;

    ngOnInit() {
        this.borderColor = (this.$showBorders() && 'gray') || 'transparent';
    }

    ngAfterViewInit(): void {
        setTimeout(() => this.loading(false), 123);
    }

    public openFileDialog(): void {
        if (!this.dragEnabled) return;

        const input = this.ensureFileInput();
        input.click();
    }

    @HostListener('dragover', ['$event']) public onDragOver(e: DragEvent) {
        e.preventDefault();
        e.stopPropagation();

        if (!this.dragEnabled) return;

        this.borderColor = '#999';
        this.borderWidth = '3px';
        this.backgroundColor = 'rgba(91, 91, 91, 0.1)';
    }

    @HostListener('dragleave', ['$event']) public onDragLeave(e: DragEvent) {
        e.preventDefault();
        e.stopPropagation();

        if (!this.dragEnabled) return;

        this.borderColor = (this.$showBorders() && 'gray') || 'transparent';
        this.borderWidth = '1px';
        this.backgroundColor = '';
    }

    @HostListener('drop', ['$event']) public onDrop(e: DragEvent) {
        e.preventDefault();
        e.stopPropagation();

        if (!this.dragEnabled) return;

        let files = e.dataTransfer!.files;
        this.onUpload(files);

        this.el.nativeElement.value = '';
        this.borderColor = (this.$showBorders() && 'gray') || 'transparent';
        this.borderWidth = '1px';
        this.backgroundColor = '';
    }

    public onUpload(files: File[] | FileList): void {
        this.loading(true);

        const collected: File[] = [];

        const push = (file: File): Observable<number> => {
            if (!this.validate(file)) return EMPTY;

            return of(file).pipe(map((result) => collected.push(result)));
        };

        let sources: Observable<number>[] = [];
        if (files.length > 0) {
            let i = 0;
            if (this.$multiple()) {
                for (; i < files.length; i++) {
                    if (i + 1 > this.$maxLimitFiles()) continue;
                    sources.push(push(files[i]));
                }
            } else sources.push(push(files[i]));
        }

        forkJoin(sources.filter((s) => !!s)).subscribe({
            next: () => collected.length > 0 && this.$files.set(collected),
            complete: () => setTimeout(() => this.loading(false), 123),
        });
    }

    alertFileNotAllowed(file: File): void {
        this.borderColor = 'red';
        this.borderWidth = 'double';

        setTimeout(() => {
            this.borderColor = (this.$showBorders() && 'gray') || 'transparent';
            this.borderWidth = '1px';
        }, 1000);

        this.invalidMotive('size', file) &&
            this.notify.error(
                'Max file size exceeded',
                'File not allowed',
                0,
                'bottom-center',
            );
        this.invalidMotive('extension', file) &&
            this.notify.error(
                'File extension not allowed',
                'File not allowed',
                0,
                'bottom-center',
            );
        this.invalidMotive('content-type', file) &&
            this.notify.error(
                'File type not allowed',
                'File not allowed',
                0,
                'bottom-center',
            );
    }

    invalidMotive(motive: 'size' | 'extension' | 'content-type', file: File): boolean {
        if (motive === 'size')
            return (
                !!this.MaxFileSize &&
                this.MaxFileSize > 0 &&
                file.size > this.MaxFileSize
            );
        if (motive === 'extension' && this.$allowedFileExtensions().length > 0) {
            const allowedFileExtensions = this.$allowedFileExtensions();

            const extensionEquals = (a: string, b: string): boolean => {
                const cleanExtension = (value: string): string => {
                    let split = value?.trim()?.split('.');
                    return split[split?.length - 1];
                };
                return stringEquals(cleanExtension(a), cleanExtension(b));
            };

            return (
                allowedFileExtensions.some((x) =>
                    extensionEquals(x, this.getExtension(file.name)),
                ) === false
            );
        }
        if (motive === 'content-type' && this.$allowedContentTypes().length > 0) {
            // Chrome lowercases macroEnabled, and a Windows drop often arrives with an empty type.
            if (!file.type?.trim()) return false;
            const fileType = file.type.toLowerCase();
            return this.$allowedContentTypes().some((type) => type.toLowerCase() === fileType) === false;
        }
        return false;
    }

    private validate(file: File): boolean {
        let invalid: boolean =
            this.invalidMotive('size', file)
            || this.invalidMotive('extension', file)
            || this.invalidMotive('content-type', file)
        ;

        invalid && this.alertFileNotAllowed(file);

        return !invalid;
    }

    /**
     * Get extension string with dot '.'
     * @param filename
     * @returns
     */
    private getExtension(filename: string) {
        let parts = filename.split('.');
        return `.${parts[parts.length - 1]}`.toLowerCase();
    }

    private get dragEnabled(): boolean {
        return this.$enabled();
    }

    private ensureFileInput(): HTMLInputElement {
        if (this.fileInput) {
            this.syncFileInputProps(this.fileInput);
            return this.fileInput;
        }

        const input = document.createElement('input');
        input.type = 'file';
        input.style.display = 'none';
        input.addEventListener('change', () => {
            if (input.files && input.files.length > 0) {
                this.onUpload(input.files);
            }
            input.value = '';
        });

        this.syncFileInputProps(input);
        this.el.nativeElement.appendChild(input);
        this.fileInput = input;

        return input;
    }

    private syncFileInputProps(input: HTMLInputElement): void {
        input.multiple = !!this.$multiple();
        input.accept = this.getAcceptList();
    }

    private getAcceptList(): string {
        const extensions = this.$allowedFileExtensions();
        if (extensions.length > 0)
            return extensions
                .map((ext) => (ext.startsWith('.') ? ext : `.${ext}`))
                .join(',');

        const contentTypes = this.$allowedContentTypes();
        if (contentTypes.length > 0)
            return contentTypes.join(',');
        return '';
    }
}
